<script setup lang="ts">
import { ref, watch, computed, onScopeDispose, nextTick, useSession, useLocalePath } from '#imports';
import { useI18n } from 'vue-i18n';
import { musicService } from '~/services/musicService';
import { useAuthState } from '~/composables/useAuthState';
import type { Source } from '~/components/mm/creator/incentives/types';
import MmDialog from '~/components/mm/base/MmDialog.vue';
import MmButton from '~/components/mm/base/MmButton.vue';
import MmIcon from '~/components/mm/base/MmIcon.vue';

const { t, locale } = useI18n();
const localePath = useLocalePath();
const { isAuthReady, isAuthenticated, user, fetchAndStoreToken } = useAuthState();
const { session } = useSession();

// 定义积分历史记录的类型
interface PointsHistoryItem {
  id: string;
  reason: number; // 积分变动原因/类型
  count: number | null; // Absolute balance change; null is unavailable, never zero.
  reason_desc: string; // 描述
  direction?: 'credit' | 'debit' | null;
  incentive_receipt_id?: string | null;
  reversal_of_entry_id?: string | null;
  source?: Source | null;
  legacy?: boolean;
  created: number | string; // Existing API timestamp in milliseconds, or a date string.
}

// 使用props和emit代替defineModel
const props = defineProps<{
  visible: boolean
}>();

const emit = defineEmits<{
  'update:visible': [value: boolean]
}>();

// 积分历史记录
const pointsHistory = ref<PointsHistoryItem[]>([]);
const isLoading = ref(false);
const hasMore = ref(true);
const currentPage = ref(1);
const pageSize = ref(10);
const loadError = ref('');
let historyRequest = 0;
let historyController: AbortController | null = null;
let failedPage = 1;
let failedAppend = false;

// 关闭弹窗
const closePopup = () => {
  resetHistory();
  emit('update:visible', false);
};
const modal = ref<InstanceType<typeof MmDialog> | null>(null);
const dialog = computed(() => modal.value?.dialog);
const closeButton = computed(() => modal.value?.closeButton);

// 格式化日期
const formatDate = (value: number | string) => {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString(locale.value);
};
// Explicit pre-W06 reason codes from music-server/pkg/repo/points_history.go.
const oldCredits = new Set([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14]);
const oldDebits = new Set([-1, -2, -3, -4, -5, -6, -7, -8, -9, -10, -11, -13, -14, -15]);
const entryDirection = (item: PointsHistoryItem) => {
  if (item.direction === 'credit' || item.direction === 'debit') return item.direction;
  // A missing pre-W06 direction may only use explicitly documented old codes.
  if (item.direction === undefined) {
    if (oldCredits.has(item.reason)) return 'credit';
    if (oldDebits.has(item.reason)) return 'debit';
  }
  return null;
};
const entryAmount = (item: PointsHistoryItem) => {
  if (item.count === null) return '—';
  const direction = entryDirection(item);
  return (direction === 'credit' ? '+' : direction === 'debit' ? '−' : '') + item.count.toLocaleString(locale.value);
};
const activityLabel = (item: PointsHistoryItem) => {
  if (typeof item.source?.title === 'string' && item.source.title.trim()) return item.source.title;
  if (typeof item.reason_desc === 'string' && item.reason_desc.trim()) return item.reason_desc;
  const known: Record<number, string> = { [-1]: 'music', [-2]: 'cover', [4]: 'returned' };
  const direction = entryDirection(item);
  return t('user.pointsActivity.' + (known[item.reason] || (direction === 'credit' ? 'added' : direction === 'debit' ? 'used' : 'other')));
};
const receiptLink = (item: PointsHistoryItem) => {
  const receipt = item.incentive_receipt_id;
  const section = item.source?.category;
  if (typeof receipt !== 'string' || !receipt.trim() || receipt.length > 128 || item.legacy !== false || item.source?.legacy !== false ||
    !section || !['newcomer', 'creator_weekly', 'badge'].includes(section)) return null;
  return localePath({ path: '/rewards', query: { section, view: 'history', receipt } });
};
const isReversal = (item: PointsHistoryItem) => typeof item.reversal_of_entry_id === 'string' && !!item.reversal_of_entry_id.trim();
const sourceContext = (item: PointsHistoryItem) => [...new Set([
  item.source?.subtitle, item.source?.content_title, item.source?.period_label,
].filter((value): value is string => typeof value === 'string' && !!value.trim() && value !== activityLabel(item)))].join(' · ');
function invalidateHistoryRequest() {
  ++historyRequest;
  historyController?.abort();
  historyController = null;
}
function resetHistory() {
  invalidateHistoryRequest();
  pointsHistory.value = [];
  currentPage.value = 1;
  hasMore.value = true;
  isLoading.value = false;
  loadError.value = '';
  failedPage = 1;
  failedAppend = false;
}

// 获取积分历史记录
const fetchPointsHistory = async (page = 1, append = false) => {
  if (isLoading.value) return;
  const owner = user.value?.id;
  const sessionId = session.value?.id;
  const language = locale.value;
  if (!props.visible || !isAuthReady.value || !isAuthenticated.value || !owner || !sessionId) return;
  const request = ++historyRequest;
  const controller = new AbortController();
  historyController = controller;
  const stillCurrent = () => request === historyRequest && props.visible && isAuthReady.value &&
    owner === user.value?.id && sessionId === session.value?.id && language === locale.value && isAuthenticated.value;
  const recoverFocus = typeof document !== 'undefined' && dialog.value?.contains(document.activeElement);
  loadError.value = '';
  isLoading.value = true;
  try {
    if (!await fetchAndStoreToken()) throw new Error('Account unavailable');
    if (!stillCurrent()) return;
    const response = await musicService.getScoreHistory({
      page,
      size: pageSize.value
    }, { musicmintInlineError: true, signal: controller.signal });
    if (!stillCurrent()) return;
    if (response?.code !== 200 || !response.data || (!Array.isArray(response.data.list) && response.data.list !== null)) {
      throw new Error('History unavailable');
    }
    const items: PointsHistoryItem[] = response.data.list || [];
    if (items.some(item => !item || typeof item.id !== 'string' || !item.id || !Number.isInteger(item.reason) ||
      (item.count !== null && (!Number.isSafeInteger(item.count) || item.count < 0)) ||
      (item.direction !== undefined && item.direction !== null && !['credit', 'debit'].includes(item.direction)))) {
      throw new Error('History unavailable');
    }
    if (append) {
      const ids = new Set(pointsHistory.value.map(item => item.id));
      pointsHistory.value = [...pointsHistory.value, ...items.filter(item => !ids.has(item.id))];
    } else {
      pointsHistory.value = items;
    }
    // 判断是否还有更多数据
    const total = response.data.total;
    hasMore.value = items.length > 0 && (typeof total === 'number' && Number.isFinite(total) && total >= 0
      ? page * pageSize.value < total : items.length === pageSize.value);
    currentPage.value = page;
  } catch {
    if (stillCurrent()) {
      failedPage = page;
      failedAppend = append;
      loadError.value = t('common.load_failed');
    }
  } finally {
    if (historyController === controller) historyController = null;
    if (stillCurrent()) {
      isLoading.value = false;
      await nextTick();
      if (recoverFocus && stillCurrent() && document.activeElement === document.body) closeButton.value?.focus();
    }
  }
};
const retryHistory = () => fetchPointsHistory(failedPage, failedAppend);

// 加载更多
const loadMore = () => {
  if (hasMore.value && !isLoading.value && !loadError.value) {
    fetchPointsHistory(currentPage.value + 1, true);
  }
};

// 监听滚动事件，实现无限滚动
const handleScroll = (event: Event) => {
  const target = event.target as HTMLElement;
  const scrollBottom = target.scrollHeight - target.scrollTop - target.clientHeight;
  
  // 当滚动到距离底部50px时，加载更多数据
  if (scrollBottom < 50 && hasMore.value && !isLoading.value && !loadError.value) {
    loadMore();
  }
};

// 监听弹窗显示状态变化
watch([() => props.visible, isAuthReady, isAuthenticated, () => user.value?.id, () => session.value?.id, locale], ([visible, ready, signedIn]) => {
  resetHistory();
  if (!visible) return;
  if (!ready) { isLoading.value = true; return; }
  if (!signedIn) { closePopup(); return; }
  void fetchPointsHistory(1, false);
}, { immediate: true, flush: 'sync' });

onScopeDispose(invalidateHistoryRequest);
</script>

<template>
  <MmDialog ref="modal" :model-value="visible" :title="t('user.pointsHistory')" @close="closePopup">
    <div class="points-history" @scroll="handleScroll">
      <div v-if="pointsHistory.length" class="points-headings" aria-hidden="true"><span class="t-eyebrow">{{ t('user.pointsActivityHeading') }}</span><span class="t-eyebrow">{{ t('user.points') }}</span></div>
      <ol v-if="pointsHistory.length" class="points-list">
        <li v-for="item in pointsHistory" :key="item.id" class="points-item">
          <div class="points-copy">
            <p>{{ activityLabel(item) }}</p>
            <span v-if="sourceContext(item)" class="points-note">{{ sourceContext(item) }}</span>
            <span v-if="isReversal(item)" class="points-note">{{ t('incPoints.reversal') }}</span>
            <span v-else-if="item.legacy" class="points-note">{{ t('incPoints.legacy') }}</span>
            <span v-if="!entryDirection(item)" class="points-note">{{ t('incPoints.unknownDirection') }}</span>
            <time>{{ formatDate(item.created) }}</time>
            <NuxtLink v-if="receiptLink(item)" :to="receiptLink(item)!" class="points-receipt" @click="closePopup">{{ t('incInsights.receipt') }}</NuxtLink>
          </div>
          <strong class="points-change t-mono" :class="{ added: entryDirection(item) === 'credit', used: entryDirection(item) === 'debit' }" :aria-label="item.count === null ? t('incInsights.unknown') : undefined">{{ entryAmount(item) }}</strong>
        </li>
      </ol>
      <div v-if="isLoading" role="status" class="history-state"><MmIcon name="refresh" class="mm-loading-icon" /><span>{{ t('common.loading') }}</span></div>
      <div v-else-if="loadError" role="alert" class="history-state history-error"><p>{{ loadError }}</p><MmButton variant="secondary" @click="retryHistory">{{ t('common.retry') }}</MmButton></div>
      <MmButton v-else-if="pointsHistory.length && hasMore" variant="secondary" class="history-more" @click="loadMore">{{ t('common.loadMore') }}</MmButton>
      <p v-else-if="pointsHistory.length" class="history-state">{{ t('common.noMoreData') }}</p>
      <div v-else class="history-state"><MmIcon name="zap" :size="32" /><p>{{ t('user.noPointsHistory') }}</p></div>
    </div>
  </MmDialog>
</template>

<style scoped>
.points-receipt:hover { color: var(--text); }
.points-history { max-height: 60dvh; overflow-y: auto; overscroll-behavior: contain; padding: var(--s-1); }
.points-list { list-style: none; padding: 0; margin: 0; }
.points-headings { display: flex; justify-content: space-between; gap: var(--s-4); padding-block: var(--s-2); }
.points-item { display: flex; align-items: center; justify-content: space-between; gap: var(--s-4); padding: var(--s-4) 0; border-bottom: 1px solid var(--line); }
.points-copy { min-width: 0; overflow-wrap: anywhere; }
.points-copy p { max-width: 68ch; font: 600 var(--t-small)/1.5 var(--font-ui); }
.points-copy time { display: block; margin-top: var(--s-2); color: var(--text-2); font: 400 var(--t-micro)/1.5 var(--font-mono); }
.points-note { display: block; margin-top: var(--s-1); color: var(--text-2); font: 400 var(--t-micro)/1.5 var(--font-ui); }
.points-receipt { display: inline-flex; align-items: center; min-height: 44px; color: var(--text-2); text-decoration: underline; text-underline-offset: var(--s-1); font: 500 var(--t-small)/1.5 var(--font-ui); }
.points-receipt:focus-visible { outline: 2px solid var(--accent-text); outline-offset: var(--s-1); }
.points-change { color: var(--text-2); flex: none; min-width: 5ch; text-align: right; white-space: nowrap; font-weight: 500; }
.points-change.added { color: var(--accent-text); }.points-change.used { color: var(--danger); }
.history-state { display: flex; flex-direction: column; align-items: center; justify-content: center; gap: var(--s-3); padding: var(--s-6) 0; text-align: center; color: var(--text-2); }
.history-error { color: var(--danger); }
.history-more { width: 100%; margin-top: var(--s-4); }
</style>
